import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FastViLogo } from "../components/FastViLogo";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Backend always returns 200 generic — no enumeration. We ignore
      // dev_reset_link intentionally; it's for tests/operators via the API.
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="w-full max-w-md card-stroke p-8 fade-up" data-testid="forgot-password-card">
        <Link to="/" className="flex items-center gap-2" data-testid="forgot-brand-home">
          <FastViLogo size={36}/>
          <span className="font-display text-2xl">Fast<span className="text-accent">Vi</span></span>
        </Link>

        {submitted ? (
          <div className="mt-6" data-testid="forgot-success">
            <div className="size-12 rounded-full bg-emerald-500/15 text-emerald-600 grid place-items-center mb-4">
              <CheckCircle2 size={20}/>
            </div>
            <div className="font-display text-3xl mb-2">Check your inbox</div>
            <p className="text-sm text-muted-foreground">
              If an account exists for <span className="font-mono text-foreground break-all">{email}</span>,
              a reset link is on its way. It's valid for 30 minutes.
            </p>
            <p className="text-xs text-muted-foreground mt-4">
              Didn't get it? Check spam, or try again in a minute.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <Link to="/login" className="flex-1">
                <Button variant="outline" className="w-full" data-testid="forgot-back-to-login">
                  <ArrowLeft size={14} className="mr-1.5"/> Back to sign in
                </Button>
              </Link>
              <Button
                variant="ghost"
                onClick={() => { setSubmitted(false); setEmail(""); }}
                data-testid="forgot-send-again"
              >
                Send again
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6" data-testid="forgot-form">
            <div className="font-display text-3xl mb-1">Forgot password?</div>
            <div className="text-sm text-muted-foreground mb-6">
              Enter the email on your FastVi account and we'll send you a reset link.
            </div>

            <div>
              <Label className="label-tiny" htmlFor="fp-email">Email</Label>
              <div className="relative mt-2">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <Input
                  id="fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@building.com"
                  className="pl-9"
                  autoFocus
                  data-testid="forgot-email"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 text-sm text-destructive" data-testid="forgot-error">{error}</div>
            )}

            <Button type="submit" className="w-full mt-6" disabled={loading || !email}
                    data-testid="forgot-submit">
              {loading ? "Sending…" : "Send reset link"}
            </Button>

            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid="forgot-back">
                <ArrowLeft size={14}/> Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
